import { useState, useEffect, useId } from 'react'
import { useT } from '../hooks'

const LIMITS = [10, 25, 50]
const DEBOUNCE_MS = 300

const SIDES = ['', 'LONG', 'SHORT']

// Labelled list controls (filter, side, rows per page) in place of the shared filter box, whose unlabelled fields read as empty boxes.
const BBookListToolbar = ({ searchLabel, searchPlaceholder, search, setSearch, side, setSide, limit, setLimit }) => {
  const { t } = useT()
  const id = useId()
  const [draft, setDraft] = useState(search ?? '')

  useEffect(() => {
    if (!setSearch || draft === search) return
    const timer = setTimeout(() => setSearch(draft), DEBOUNCE_MS) // one request once typing pauses, not one per key
    return () => clearTimeout(timer)
  }, [draft])

  return (
    <div className='b_book_toolbar'>
      {setSearch && (
        <div className='b_book_toolbar_field b_book_toolbar_search'>
          <label htmlFor={`${id}-search`}>{searchLabel}</label>
          <input id={`${id}-search`} type='search' value={draft} placeholder={searchPlaceholder} onChange={(e) => setDraft(e.target.value)} />
        </div>
      )}
      {setSide && (
        <div className='b_book_toolbar_field'>
          <label htmlFor={`${id}-side`}>{t('pages.b_book.filters.side')}</label>
          <select id={`${id}-side`} value={side || ''} onChange={(e) => setSide(e.target.value || null)}>
            {SIDES.map((value) => <option key={value} value={value}>{t(`pages.b_book.filters.side_${value ? value.toLowerCase() : 'all'}`)}</option>)}
          </select>
        </div>
      )}
      {setLimit && (
        <div className='b_book_toolbar_field'>
          <label htmlFor={`${id}-limit`}>{t('pages.b_book.rows_per_page')}</label>
          <select id={`${id}-limit`} value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
            {LIMITS.map((value) => <option key={value} value={value}>{value}</option>)}
          </select>
        </div>
      )}
    </div>
  )
}

// Previous / next with where you are spelled out: `pageCount` for a list paged here, `hasNext` for a server-paged one whose total is unknown.
export const BBookPager = ({ page, pageCount, hasNext, onChange }) => {
  const { t } = useT()
  const last = pageCount !== undefined ? page >= pageCount - 1 : !hasNext
  if (page === 0 && last) return null
  return (
    <nav className='b_book_pager' aria-label={t('pages.b_book.pager.label')}>
      <button className='b_book_row_button' disabled={page === 0} onClick={() => onChange(page - 1)}>{t('pages.b_book.pager.previous')}</button>
      <span className='b_book_num' aria-live='polite'>
        {pageCount !== undefined ? t('pages.b_book.pager.position', { page: page + 1, count: pageCount }) : t('pages.b_book.pager.page', { page: page + 1 })}
      </span>
      <button className='b_book_row_button' disabled={last} onClick={() => onChange(page + 1)}>{t('pages.b_book.pager.next')}</button>
    </nav>
  )
}

export default BBookListToolbar
